"use client";

import { c as _c } from "react/compiler-runtime";
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TalkToLearningAdvisorForm } from "@/components/forms/talk-to-learning-advisor-form";
import { cn } from "@/lib/utils";
import { DownloadBrochureForm } from "@/components/forms/download-brochure-form";
import Image from "next/image";
import { BannerCorporateEnquiryPopupForm } from "@/components/forms/banner-corporate-enquiry-popup-form";
import { EnquiryForm } from "@/components/forms/enquiry-form";
type PopupType = "talk-to-advisor" | "course-enquiry" | "request-callback" | "drop-a-query" | "download-brochure" | "corporate-enquiry" | "enquiry" | "custom";
type PopupOptions = {
  type: PopupType;
  title?: string;
  description?: string;
  data?: Record<string, unknown>;
  content?: ReactNode;
};
type PopupContextType = {
  openPopup: (options: PopupOptions) => void;
  closePopup: () => void;
};
const PopupContext = createContext<PopupContextType | null>(null);
export function PopupProvider(t0) {
  const $ = _c(39);
  const {
    children
  } = t0;
  const [popup, setPopup] = useState(null);
  const [open, setOpen] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = options => {
      setPopup(options);
      setOpen(true);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const openPopup = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setOpen(false);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const closePopup = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      openPopup,
      closePopup
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const contextValue = t3;
  const t4 = popup?.type !== "custom";
  const t5 = popup?.type === "download-brochure";
  const t6 = popup?.type === "corporate-enquiry";
  let t7;
  if ($[3] !== t4 || $[4] !== t5 || $[5] !== t6) {
    t7 = cn("sm:max-w-lg", {
      "p-0": t4,
      "grid gap-0 overflow-hidden p-0 sm:max-w-3xl md:grid-flow-col md:grid-cols-[1fr_2fr]": t5,
      "grid gap-0 overflow-hidden p-0 sm:max-w-3xl md:grid-flow-col md:grid-cols-12": t6
    });
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== popup) {
    t8 = popup?.type === "custom" && popup.content && <DialogHeader>{popup?.title && <DialogTitle>{popup.title}</DialogTitle>}{popup?.description && <DialogDescription>{popup.description}</DialogDescription>}</DialogHeader>;
    $[7] = popup;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== popup?.data || $[10] !== popup?.description || $[11] !== popup?.title || $[12] !== popup?.type) {
    t9 = popup?.type === "talk-to-advisor" && <TalkToLearningAdvisorForm heading={popup?.title} subHeading={popup?.description} onSuccess={closePopup} {...popup?.data} />;
    $[9] = popup?.data;
    $[10] = popup?.description;
    $[11] = popup?.title;
    $[12] = popup?.type;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== popup?.data || $[15] !== popup?.type) {
    t10 = popup?.type === "download-brochure" && <><div className="relative hidden md:block"><Image src={popup?.data?.serviceId ? "/images/popup/download-brochure-popup-course.webp" : "/images/popup/download-brochure-popup.webp"} alt="spoclearn-download-banner" quality={75} fill={true} className="h-full w-full object-cover object-center select-none" sizes="307px" /></div><DownloadBrochureForm {...popup?.data} onSuccess={closePopup} /></>;
    $[14] = popup?.data;
    $[15] = popup?.type;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== popup?.data || $[18] !== popup?.type) {
    t11 = popup?.type === "corporate-enquiry" && <><div className="relative hidden md:col-span-5 md:flex"><Image src="/images/popup/spoclearn-corporate-popup-img.avif" alt="spoclearn-download-banner" quality={75} fill={true} className="h-full w-full object-cover object-center select-none" sizes="307px" /></div><div className="md:col-span-7"><BannerCorporateEnquiryPopupForm {...popup?.data} onSuccess={closePopup} /></div></>;
    $[17] = popup?.data;
    $[18] = popup?.type;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== popup?.data || $[21] !== popup?.description || $[22] !== popup?.title || $[23] !== popup?.type) {
    t12 = popup?.type === "enquiry" && <EnquiryForm title={popup?.title} description={popup?.description} {...popup?.data} onSuccess={closePopup} />;
    $[20] = popup?.data;
    $[21] = popup?.description;
    $[22] = popup?.title;
    $[23] = popup?.type;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const t13 = popup?.type === "custom" && popup.content;
  let t14;
  if ($[25] !== t10 || $[26] !== t11 || $[27] !== t12 || $[28] !== t13 || $[29] !== t7 || $[30] !== t8 || $[31] !== t9) {
    t14 = <DialogContent className={t7}>{t8}{t9}{t10}{t11}{t12}{t13}</DialogContent>;
    $[25] = t10;
    $[26] = t11;
    $[27] = t12;
    $[28] = t13;
    $[29] = t7;
    $[30] = t8;
    $[31] = t9;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== open || $[34] !== t14) {
    t15 = <Dialog open={open} onOpenChange={setOpen}>{t14}</Dialog>;
    $[33] = open;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== children || $[37] !== t15) {
    t16 = <PopupContext.Provider value={contextValue}>{children}{t15}</PopupContext.Provider>;
    $[36] = children;
    $[37] = t15;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  return t16;
}
export function usePopup() {
  const context = useContext(PopupContext);
  if (!context) {
    throw new Error("usePopup must be used within PopupProvider");
  }
  return context;
}